import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { useAccess } from '../../../../hooks/useAccess';
import { SubscriptionAccess } from '../../../../types/Subscription';
import { selectIsTrialEligible } from '../../../../slices/subscriptions/subscriptionsSlice';
import { examsSelectors } from '../../../../slices/exams/examsSlice';
import { isNclexExam } from '../../../../utils/examUtils';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@/components/ui/modal';
import DailyLimits from '../DailyLimits';
import { Button } from '@/components/ui/button';
import { capture } from '../../../../utils/analytics';
import {
  getUnlimitedAccessCtaLabel,
  getUpgradeCtaLabel,
} from '../../../../utils/upgradeCopy';

interface UpgradeModalProps {
  open: boolean;
  close: () => void;
  contentType:
    | 'Video'
    | 'QBank'
    | 'Upload'
    | 'AI Chat'
    | 'Audio Overview'
    | 'Article Audio Overview'
    | 'Question Coach';
}

export type UpgradeModalLimitType =
  | 'video'
  | 'qbank'
  | 'upload'
  | 'ai_chat'
  | 'audio_overview'
  | 'question_coach';

export const mapContentTypeToLimitType = (
  contentType: UpgradeModalProps['contentType']
): UpgradeModalLimitType => {
  switch (contentType) {
    case 'Video':
      return 'video';
    case 'QBank':
      return 'qbank';
    case 'Upload':
      return 'upload';
    case 'AI Chat':
      return 'ai_chat';
    case 'Audio Overview':
    case 'Article Audio Overview':
      return 'audio_overview';
    case 'Question Coach':
      return 'question_coach';
    default:
      return 'qbank';
  }
};

/**
 * Upsells that are not about today's cap.
 *
 * Most of this modal answers "you have used your allowance for today", and it
 * shows the limit table to prove it. A few things are simply not on the free
 * plan at all: a student who taps one of those still has chats left, and
 * telling them their limit is reached is plainly wrong (ORA-459). Those get a
 * title and a line of their own, and no limit table.
 *
 * Audio Overviews (ORA-735) are paid-only: a free student can set one up but
 * not generate or play it. A library article's episode (ORA-905) is the same
 * feature with its own wording. So is the Question Coach walkthrough (ORA-1091).
 */
interface FeatureUpsellCopy {
  title: string;
  body: string;
  /** Replaces `body` for a student who can no longer start the free trial. */
  trialIneligibleBody?: string;
}

const FEATURE_UPSELL_COPY: Partial<
  Record<UpgradeModalProps['contentType'], FeatureUpsellCopy>
> = {
  'Audio Overview': {
    title: 'Listen to Your Lectures',
    body: 'Audio Overviews turn your lectures into a two-host, podcast-style episode you can play on a commute, at the gym or between classes. They are included with every paid plan.',
  },
  'Article Audio Overview': {
    title: 'Listen to This Article',
    body: 'Audio Overviews turn library articles into a two-host, podcast-style episode you can play on a commute, at the gym or between classes. They are included with every paid plan.',
  },
  'Question Coach': {
    title: 'Question Coach Is Part of Ora Pro',
    body: 'Question Coach is a narrated walkthrough of how to approach each question, with the key phrases highlighted as it plays. It is included with every paid plan and the free trial.',
    trialIneligibleBody:
      'Question Coach is a narrated walkthrough of how to approach each question, with the key phrases highlighted as it plays. It is included with every paid plan.',
  },
};

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  open,
  close,
  contentType,
}) => {
  const { accessLevel } = useAccess();
  const isTrialEligible = useSelector(selectIsTrialEligible);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const limitType = mapContentTypeToLimitType(contentType);
  const featureUpsell = FEATURE_UPSELL_COPY[contentType];
  const hasFiredShownRef = useRef(false);
  // Same rule as the NavBar's upgrade button: no purchase CTA in the Android
  // app. Scoped to feature upsells, which are new; the daily-limit variant is
  // unchanged.
  const showCta = !(featureUpsell && Capacitor.getPlatform() === 'android');

  useEffect(() => {
    if (open && !hasFiredShownRef.current) {
      hasFiredShownRef.current = true;
      capture('upgrade_modal_shown', { limitType });
    } else if (!open) {
      hasFiredShownRef.current = false;
    }
  }, [open, limitType]);

  const renderDescription = () => {
    if (accessLevel === SubscriptionAccess.PRE_CLINICAL) {
      // A nursing student never studies Step or Shelf exams.
      return isNclexExam(primaryExam)
        ? 'On the Preclinical plan, your daily studying is limited on this exam.'
        : 'On the Preclinical plan, your daily studying is limited on Step 2 and Shelf exams.';
    } else {
      return 'On the Free plan, your daily studying is limited for each exam.';
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      name={'upgrade'}
      className="modal--glass"
    >
      <ModalHeader className="modal_header">
        <h2>
          {featureUpsell
            ? featureUpsell.title
            : `Today's ${contentType} Limit Reached`}
        </h2>
      </ModalHeader>
      <ModalBody className="modal_content">
        <div>
          {featureUpsell
            ? (!isTrialEligible && featureUpsell.trialIneligibleBody) ||
              featureUpsell.body
            : renderDescription()}
        </div>
        {featureUpsell ? null : <DailyLimits />}
      </ModalBody>
      {showCta ? (
        <ModalFooter className="modal_actions">
          <Button
            className="!w-full"
            variant="brand-gradient"
            onClick={() => {
              capture('upgrade_modal_cta_clicked', { limitType });
              window.open('/pricing', '_blank');
            }}
          >
            {/* A paid-only feature is not about "unlimited" anything. */}
            {featureUpsell
              ? getUpgradeCtaLabel(isTrialEligible)
              : getUnlimitedAccessCtaLabel(isTrialEligible)}
          </Button>
        </ModalFooter>
      ) : null}
    </Modal>
  );
};

export default UpgradeModal;
